import type { FirebaseApp } from "firebase/app";
import { getAnalytics, isSupported, logEvent, setUserProperties, type Analytics } from "firebase/analytics";
import { Capacitor } from "@capacitor/core";
import type { FirebaseWebConfig } from "./config.js";

/**
 * Firebase Analytics (GA4), with two backends:
 *
 *   - **Native** (`@capacitor-firebase/analytics`) on Android release builds
 *     that were given a google-services.json. Events land in GA4's
 *     *Android app* stream, which is what Google Ads needs to import
 *     `first_open` / in-app actions (e.g. `select_content`) as App campaign
 *     conversions. Opt-in via `VITE_NATIVE_ANALYTICS=true`, which the Android
 *     release workflows set only when they restored google-services.json —
 *     without that file the native SDK silently drops events, so the flag
 *     keeps us on the web SDK instead of going dark.
 *   - **Web SDK** everywhere else (browser build, iOS, Android builds without
 *     native config). Events land in the GA4 Web data stream.
 *
 * Exactly one backend is active per session, so nothing is double-counted.
 *
 * `isSupported()` resolves false in contexts without IndexedDB (Safari
 * private mode, some webviews) — analytics silently no-ops there instead
 * of throwing, same tolerance pattern as ads.ts and config.ts.
 */

type NativeAnalytics = typeof import("@capacitor-firebase/analytics").FirebaseAnalytics;

let analytics: Analytics | undefined;
let native: NativeAnalytics | undefined;
let booted = false;

export function shouldUseNativeAnalytics(env: ImportMetaEnv = import.meta.env): boolean {
  return env.VITE_NATIVE_ANALYTICS === "true" && Capacitor.getPlatform() === "android";
}

export async function bootAnalytics(app: FirebaseApp, config: FirebaseWebConfig): Promise<void> {
  if (booted) return;
  booted = true;

  if (shouldUseNativeAnalytics()) {
    try {
      const mod = await import("@capacitor-firebase/analytics");
      native = mod.FirebaseAnalytics;
      await native.setEnabled({ enabled: true });
      return;
    } catch (err) {
      native = undefined;
      console.warn("[analytics] native plugin unavailable, falling back to web SDK:", err);
    }
  }

  if (!config.measurementId) return;
  try {
    if (!(await isSupported())) return;
    analytics = getAnalytics(app);
  } catch (err) {
    console.warn("[analytics] failed to initialize:", err);
  }
}

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (native) {
    // App streams report screens as `screen_view`; a WebView SPA has one
    // Activity, so route changes must be logged explicitly.
    const call =
      name === "page_view"
        ? native.setCurrentScreen({ screenName: String(params?.["page_path"] ?? "/") })
        : native.logEvent({ name, params: params as Record<string, unknown> | undefined });
    call.catch((err) => console.warn("[analytics] native event failed:", name, err));
    return;
  }
  if (!analytics) return;
  try {
    logEvent(analytics, name, params);
  } catch (err) {
    console.warn("[analytics] failed to log event:", name, err);
  }
}

export function setUserProps(props: Record<string, string | number | boolean | null>): void {
  if (native) {
    for (const [key, value] of Object.entries(props)) {
      native
        .setUserProperty({ key, value: value === null ? null : String(value) })
        .catch((err) => console.warn("[analytics] native user property failed:", key, err));
    }
    return;
  }
  if (!analytics) return;
  try {
    setUserProperties(analytics, props);
  } catch (err) {
    console.warn("[analytics] failed to set user properties:", err);
  }
}
